/**
 * @file     MortgageToolLayout.tsx
 * @purpose  The family 7 tool panel: inputs on the left and the live result on
 *           the right, stacked inputs-first when the container is narrow. Sized
 *           by a container query so the same tool fits a page and a lesson
 *           widget. The result column stays in view while the inputs scroll.
 */
'use client';

import Box from '@mui/material/Box';
import { hairline, hubColors, hubRadius } from '@/components/hub/hubKitStyles';

type MortgageToolLayoutProps = {
  inputs: React.ReactNode;
  result: React.ReactNode;
  /** Accessible name for the inputs group. */
  inputsLabel: string;
  /** Fires on the first and every later edit inside the inputs (typing, slider, chip). */
  onInput?: () => void;
};

export const TOOL_WIDE = '@container mortgage-tool (min-width: 760px)';

export function MortgageToolLayout({ inputs, result, inputsLabel, onInput }: MortgageToolLayoutProps) {
  return (
    <Box sx={{ containerType: 'inline-size', containerName: 'mortgage-tool' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 2,
          alignItems: 'start',
          [TOOL_WIDE]: { gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)', gap: 3 },
        }}
      >
        <Box
          role="group"
          aria-label={inputsLabel}
          onInput={onInput}
          onKeyDown={onInput}
          onPointerDown={onInput}
          sx={{
            minWidth: 0,
            border: hairline,
            borderRadius: `${hubRadius.panel}px`,
            backgroundColor: hubColors.surfaceCard,
            p: { xs: 4, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {inputs}
        </Box>
        <Box sx={{ minWidth: 0, [TOOL_WIDE]: { position: 'sticky', top: 'calc(var(--chrome-inset, 0px) + 88px)' } }}>{result}</Box>
      </Box>
    </Box>
  );
}
